import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  Divider,
  Flex,
  Heading,
  Text,
} from "@aws-amplify/ui-react";
import { useEmergencyContactsQuery } from "../profile/api/useEmergencyContactsQuery";
import { useUserQuery } from "../common/useUserQuery";
import { useMedicationsQuery } from "../healthRecords/api/useMedicationsQuery";
import { useFactsSheetsQuery } from "./api/useFactsSheetQuery";
import { useAllergiesQuery } from "../healthRecords/api/useAllergiesQuery";
import { useCareProvidersQuery } from "../careProviders/api/useCareProvidersQuery";
import { handleDownloadPdf } from "../../pdfGenerator/pdfGenerator";
import { useImmunizationsQuery } from "../healthRecords/api/useImmunizationsQuery";
import { useSymptomsQuery } from "../healthRecords/api/useSymptomsQuery";
import { useMedicalConditionsQuery } from "../healthRecords/api/useMedicalConditionQuery";
import { useLifeStyleQuery } from "../lifestyle/api/useLifeStyleQuery";
import { useProfileQuery } from "../profile/api/useProfileQuery";

export default function FactsSheetDownload() {
  const printRef = useRef(null);

  const { data: userQueryData, isFetching: userQueryDataIsFetching } =
    useUserQuery();
  const { data: profileData, isFetching: profileDataIsFetching } =
    useProfileQuery(userQueryData?.patientUserId);
  const {
    data: emergencyContactData,
    isFetching: emergencyContactDataIsFetching,
  } = useEmergencyContactsQuery(userQueryData?.patientUserId);
  const { data: medicationsData, isFetching: medicationsDataIsFetching } =
    useMedicationsQuery(userQueryData?.patientUserId);
  const { data: factsSheetData, isFetching: factsSheetDataIsFetching } =
    useFactsSheetsQuery(userQueryData?.patientUserId);
  const { data: allergyData, isFetching: allergyDataIsFetching } =
    useAllergiesQuery(userQueryData?.patientUserId);
  const { data: careProviderData, isFetching: careProviderDataIsFetching } =
    useCareProvidersQuery(userQueryData?.patientUserId);
  const {
    data: immunizationResults,
    isFetching: immunizationResultsIsFetching,
  } = useImmunizationsQuery(userQueryData?.patientUserId);
  const { data: symptomResults, isFetching: symptomResultsIsFetching } =
    useSymptomsQuery(userQueryData?.patientUserId);
  const {
    data: medicalConditionResults,
    isFetching: medicalConditionResultsIsFetching,
  } = useMedicalConditionsQuery(userQueryData?.patientUserId);
  const { data: lifestyleData, isFetching: lifestyleDataIsFetching } =
    useLifeStyleQuery(userQueryData?.patientUserId);

  const downloadPDF = async () => {
    if (printRef.current) {
      let element = printRef.current.cloneNode(true);
      element.firstChild.remove();
      const filename = "Facts-Sheet.pdf";
      await handleDownloadPdf(element, filename);
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    const cleaned = ("" + phoneNumber).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`;
    }
    return phoneNumber;
  };

  if (
    profileDataIsFetching ||
    userQueryDataIsFetching ||
    emergencyContactDataIsFetching ||
    medicationsDataIsFetching ||
    factsSheetDataIsFetching ||
    allergyDataIsFetching ||
    careProviderDataIsFetching ||
    immunizationResultsIsFetching ||
    symptomResultsIsFetching ||
    medicalConditionResultsIsFetching ||
    lifestyleDataIsFetching
  ) {
    return <div>Loading...</div>;
  }

  return (
    <Flex
      direction="column"
      padding="32px"
      backgroundColor="rgba(250, 250, 250, 1)"
      gap="20px"
      ref={printRef}
    >
      <Flex justifyContent="flex-end" id="downloadButtonSection">
        <Button variation="primary" type="submit" onClick={downloadPDF}>
          Download PDF
        </Button>
      </Flex>

      <Card variation="elevated" padding="20px">
        <Heading level="5">Name: {profileData?.givenName}</Heading>

        {profileData?.nickname && (
          <Text>Nickname: {profileData?.nickname}</Text>
        )}
      </Card>

      <Card variation="elevated" padding="20px">
        <Heading level="5" marginBottom="10px">
          Emergency Information
        </Heading>
        <Divider marginBottom="10px" />
        <Heading level="6">In case of an emergency, please dial 911.</Heading>
      </Card>

      {careProviderData.some(
        (provider) => provider.isPrimaryProvider === 1
      ) && (
        <Card variation="elevated" padding="20px">
          <Heading level="5" marginBottom="10px">
            Primary Doctor
          </Heading>
          <Divider marginBottom="10px" />
          {careProviderData
            .filter((provider) => provider.isPrimaryProvider === 1)
            ?.map((provider) => (
              <div key={provider.id} style={{ marginBottom: "10px" }}>
                <Text fontWeight={600}>{provider.providerName}</Text>
                <Text>{provider.providerSpecialty}</Text>
                <Text>{formatPhoneNumber(provider.providerPhone)}</Text>
              </div>
            ))}
        </Card>
      )}
      {emergencyContactData?.length > 0 && (
        <Card variation="elevated" padding="20px">
          <Heading level="5" marginBottom="10px">
            Emergency Contact
          </Heading>
          <Divider marginBottom="10px" />
          {emergencyContactData.map((contact) => (
            <div key={contact.id} style={{ marginBottom: "10px" }}>
              <Text fontWeight={600}>Emergency Contact: {contact.name}</Text>
              <Text>Relationship: {contact.relationship}</Text>
              <Text>Phone: {formatPhoneNumber(contact.phone)}</Text>
            </div>
          ))}
        </Card>
      )}
      {immunizationResults.length > 0 && (
        <Card variation="elevated" padding="20px">
          <Heading level="5" marginBottom="10px">
            Immunizations
          </Heading>
          <Divider marginBottom="10px" />
          {immunizationResults.map((immunization) => (
            <div key={immunization.id} style={{ marginBottom: "10px" }}>
              <Text fontWeight={600}>
                Immunization Name: {immunization.immunizationName}
              </Text>
              <Text>Immunization Method: {immunization.method}</Text>
              <Text>Immunization Dosage: {immunization.dosage}</Text>
              <Text>
                Immunization Administered Dates:{" "}
                {JSON.parse(immunization?.administeredDates)?.join(" | ")}
              </Text>
            </div>
          ))}
        </Card>
      )}
      {medicationsData.some((medication) => medication.isActive === 1) && (
        <Card variation="elevated" padding="20px">
          <Heading level="5" marginBottom="10px">
            Medications
          </Heading>
          <Divider marginBottom="10px" />
          {medicationsData
            .filter((medication) => medication.isActive === 1)
            ?.map((medication) => (
              <div key={medication.id} style={{ marginBottom: "10px" }}>
                <Text fontWeight={600}>
                  Medication Name: {medication.medicationName}
                </Text>
                <Text>Medication Usage: {medication.usage}</Text>
                <Text>Medication Dosage: {medication.dosage}</Text>
              </div>
            ))}
        </Card>
      )}
      {allergyData.some((allergy) => allergy.isActive === 1) && (
        <Card variation="elevated" padding="20px">
          <Heading level="5" marginBottom="10px">
            Allergies
          </Heading>
          <Divider marginBottom="10px" />
          {allergyData
            .filter((allergy) => allergy.isActive === 1)
            .sort((a, b) => a.allergyLevel.localeCompare(b.allergyLevel))
            ?.map((allergy) => (
              <div key={allergy.id} style={{ marginBottom: "10px" }}>
                <Text fontWeight={600}>
                  Allergy Name: {allergy.allergyName}
                </Text>
                <Text>Allergy Level: {allergy.allergyLevel}</Text>
              </div>
            ))}
        </Card>
      )}
      {medicalConditionResults?.length > 0 && (
        <Card variation="elevated" padding="20px">
          <Heading level="5" marginBottom="10px">
            Medical Conditions
          </Heading>
          <Divider marginBottom="10px" />
          {medicalConditionResults?.map((condition) => (
            <div key={condition.id} style={{ marginBottom: "10px" }}>
              <Text fontWeight={600}>
                Diagnosis Name: {condition.diagnosisName}
              </Text>
              <Text>
                Diagnosis Description: {condition.diagnosisDescription}
              </Text>
              <Text>Status: {condition.medicalConditionStage}</Text>
            </div>
          ))}
        </Card>
      )}
      {symptomResults?.length > 0 && (
        <Card variation="elevated" padding="20px">
          <Heading level="5" marginBottom="10px">
            Symptom
          </Heading>
          <Divider marginBottom="10px" />
          {symptomResults?.map((symptom) => (
            <div key={symptom.id} style={{ marginBottom: "10px" }}>
              <Text fontWeight={600}>Symptom: {symptom?.symptom}</Text>
              <Text>
                Symptom When Experienced: {symptom?.howStartExperience}
              </Text>
              <Text>Symptom Start Date: {symptom?.symptomStartDate}</Text>
              <Text> Symptom End Date: {symptom?.symptomEndDate}</Text>
            </div>
          ))}
        </Card>
      )}
      <Card variation="elevated" padding="20px">
        <Heading level="5" marginBottom="10px">
          Lifestyle
        </Heading>
        <Divider marginBottom="10px" />
        <div style={{ marginBottom: "10px" }}>
          <Text>Alcohol Use: {lifestyleData?.alcohol}</Text>
          <Text>Tobacco Use: {lifestyleData?.tabacco}</Text>
          <Text>
            Recreational Drug Use: {lifestyleData?.recreational_drugs}
          </Text>
        </div>
      </Card>
      {factsSheetData?.length > 0 && (
        <Card variation="elevated" padding="20px">
          <Heading level="5" marginBottom="10px">
            Facts
          </Heading>
          <Divider marginBottom="10px" />
          {factsSheetData?.map((factsSheet) => (
            <div key={factsSheet?.id} style={{ marginBottom: "10px" }}>
              <Text fontWeight={600}>{factsSheet?.sectionTitle}</Text>
              <Text>{factsSheet?.sectionDescription}</Text>
            </div>
          ))}
        </Card>
      )}
    </Flex>
  );
}
